import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

/**
 * Movement for a list that is reordered and removed from in place.
 *
 * A list reordered by pressing ↑ or ↓ changes instantly, and an instant change
 * is one nobody can follow: two rows swap, and the only way to tell what
 * happened — or whether anything happened at all — is to stop and re-read both.
 * Removal is the same problem in a worse place: a row disappears, everything
 * below jumps up, and on a phone, where the list runs well past the fold, the
 * jump is the only feedback that the right thing went.
 *
 * Both are answered by the same trick. Remember where every registered element
 * was, let React put them where they now belong, and animate each one from the
 * position it had to the position it has — so the change is something that
 * happens *to* the row rather than something that has already happened by the
 * time you look. Removals run it in reverse: the row fades where it stands
 * before it leaves the list, so the gap it left closes as a movement.
 *
 * Nothing here is load-bearing. Every position animated is one the browser has
 * already committed to, so a machine with no Web Animations API — which
 * includes jsdom, where the components using this are tested — and a reader who
 * has asked for reduced motion both get the instant behavior this replaced,
 * with `leave` running its removal straight away rather than waiting on an
 * animation that will never start.
 */

/** Long enough to be followed, short enough not to delay the next press. */
const MOVE_MS = 220;
/** A removal is a shorter beat than a move: it is confirmation, not tracking. */
const LEAVE_MS = 160;
/** Quick off the mark and slow into place, so the destination is what reads. */
const MOVE_EASING = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

type MotionRef = (node: HTMLElement | null) => (() => void) | void;

/** Where a row was, and — the part that says whether it *moved* — what it was. */
interface Placement {
  left: number;
  top: number;
  /**
   * The row's place among its siblings.
   *
   * This is what separates the list changing from the page changing. Rows shift
   * about for all sorts of reasons that are nobody's list operation — a name
   * typed until the chips rewrap, a hint appearing under a select, a phone
   * turned on its side — and sliding rows around on each of those is at best
   * noise and at worst, on a field that re-renders per keystroke, a list that
   * will not sit still while it is being typed into. Coordinates say how far to
   * animate; this says whether to.
   */
  order: number;
}

function placementOf(node: HTMLElement): Placement {
  const parent = node.parentElement;
  return {
    // Offsets rather than a bounding rect: these are measured from the offset
    // parent instead of the viewport, so a page scrolled between two readings
    // does not read as every row having moved by the scroll distance.
    left: node.offsetLeft,
    top: node.offsetTop,
    order: parent ? Array.prototype.indexOf.call(parent.children, node) : 0,
  };
}

/**
 * How far a row is still from where it was last told to go.
 *
 * Pressing ↓ twice in quick succession catches the first move mid-flight. The
 * layout has already moved on, so a second animation starting from the layout
 * would begin by snapping the row to where the first move was heading — a jump,
 * in the middle of the one interaction this hook exists to make followable.
 * Carrying the leftover into the new animation's first frame keeps the row
 * where the eye last had it.
 */
function carriedOffset(node: HTMLElement): { x: number; y: number } {
  const none = { x: 0, y: 0 };
  // jsdom has neither, and a browser old enough to lack DOMMatrix simply gets
  // the jump — the animation is a nicety, not the behavior.
  if (typeof DOMMatrixReadOnly !== 'function' || typeof window.getComputedStyle !== 'function') return none;
  const transform = window.getComputedStyle(node).transform;
  if (!transform || transform === 'none') return none;
  try {
    const matrix = new DOMMatrixReadOnly(transform);
    return { x: matrix.m41, y: matrix.m42 };
  } catch {
    return none;
  }
}

export interface ListMotion {
  /**
   * Ref callback for the element standing in for `key` in the list.
   *
   * The same callback every time it is asked for, which is not a nicety: a ref
   * whose identity changes is detached and reattached on every render, and this
   * one's whole job is to hold on to something from the render before.
   */
  register: (key: string) => MotionRef;
  /**
   * Fades out the elements behind `keys`, then runs `commit` — exactly once —
   * to take the thing they show out of the list for real.
   *
   * More than one key because a single row can be on screen in more than one
   * place: a room is both a chip in the summary and a card below it, and both
   * should go at the same moment.
   */
  leave: (keys: readonly string[], commit: () => void) => void;
}

function motionAllowed(node: HTMLElement): boolean {
  if (typeof node.animate !== 'function') return false;
  return !(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false);
}

export function useListMotion(): ListMotion {
  const nodes = useRef(new Map<string, HTMLElement>());
  const refs = useRef(new Map<string, MotionRef>());
  const placements = useRef(new Map<string, Placement>());
  const leaving = useRef(new Set<string>());
  /** The move each row is in the middle of, so the next one can take it over. */
  const moving = useRef(new Map<string, Animation>());

  /** Where every registered row is, and what it is, right now. */
  const measure = useCallback(() => {
    const taken = new Map<string, Placement>();
    for (const [key, node] of nodes.current) taken.set(key, placementOf(node));
    return taken;
  }, []);

  /*
    A reflow is not a move — and where a row sits among its siblings is what
    tells them apart, so the rewraps and re-heights above are already ignored.
    What is left is the distance: a phone turned on its side moves every row
    without React rendering anything, and the *next* genuine reorder would then
    be animated in from wherever the row was before the rotation.

    Re-reading rather than throwing the measurements away, so a reorder in the
    same breath as a resize still animates, from where the rows actually are.
  */
  useEffect(() => {
    const restate = () => {
      placements.current = measure();
    };
    window.addEventListener('resize', restate);
    return () => window.removeEventListener('resize', restate);
  }, [measure]);

  /*
    Measured after every commit rather than only after the ones that reorder,
    because a row that grew — a hint appearing under a select, a name wrapping
    to a second line — moves everything below it, and comparing the next press
    against positions taken before that would animate rows in from somewhere
    they never were.
  */
  useLayoutEffect(() => {
    const before = placements.current;
    const after = measure();

    for (const [key, to] of after) {
      const from = before.get(key);
      // Nothing to come from: this row has only just appeared.
      if (!from) continue;
      // A leaving row is mid-fade where it stands, and the transform this would
      // hand it would fight the one already running on it.
      if (leaving.current.has(key)) continue;
      // The row is where it was in the list; the page moved around it. Take the
      // new measurement and say nothing about it.
      if (from.order === to.order) continue;

      const node = nodes.current.get(key);
      if (!node || !motionAllowed(node)) continue;

      const carried = carriedOffset(node);
      const dx = from.left - to.left + carried.x;
      const dy = from.top - to.top + carried.y;
      // Sub-pixel drift out of a reflow is not a move anybody asked to watch.
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;

      // Read first, then take the old one down: the leftover above is only
      // there to be read while it is still running.
      moving.current.get(key)?.cancel();
      const animation = node.animate(
        [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0px, 0px)' }],
        { duration: MOVE_MS, easing: MOVE_EASING },
      );
      moving.current.set(key, animation);
      const settled = () => {
        if (moving.current.get(key) === animation) moving.current.delete(key);
      };
      animation.finished.then(settled, settled);
    }

    placements.current = after;
  });

  return useMemo<ListMotion>(
    () => ({
      register(key) {
        const existing = refs.current.get(key);
        if (existing) return existing;

        const ref: MotionRef = (node) => {
          if (node) nodes.current.set(key, node);
          /*
            Only the element is let go here. Dropping the callback with it —
            the tidier-looking thing to do — quietly breaks everything above:
            React runs this cleanup whenever a ref is detached, the next render
            would then hand it a *different* callback for the same key, and
            React detaches that one too. Every render from then on is a full
            detach and reattach, and a hook that works by comparing this render
            to the last one never gets a last one. What is left behind is one
            closure per key ever seen, in a list somebody is editing by hand.
          */
          return () => {
            nodes.current.delete(key);
          };
        };
        refs.current.set(key, ref);
        return ref;
      },

      leave(keys, commit) {
        // Already on its way out. The press that started it is the one that
        // counts: committing a second time would take a second row with it.
        if (keys.some((key) => leaving.current.has(key))) return;

        const running: Array<Promise<unknown>> = [];
        for (const key of keys) {
          const node = nodes.current.get(key);
          if (!node || !motionAllowed(node)) continue;
          leaving.current.add(key);
          // Wherever it was heading no longer matters, and two animations on
          // the same transform is a fight the later one wins anyway.
          moving.current.get(key)?.cancel();
          moving.current.delete(key);
          // It is still on the page for the length of the fade, and a row that
          // is half gone is not one to be able to press.
          node.style.pointerEvents = 'none';
          const animation = node.animate([{ opacity: 1 }, { opacity: 0, transform: 'scale(0.98)' }], {
            duration: LEAVE_MS,
            easing: 'ease-in',
            // Held on the last frame, or the row snaps back to full opacity for
            // the frame between the animation ending and React removing it.
            fill: 'forwards',
          });
          running.push(animation.finished.catch(() => undefined));
        }

        // Nothing could be animated — no Web Animations API, reduced motion, or
        // the row is not on screen at all. Then this is the plain removal it
        // has always been.
        if (running.length === 0) {
          commit();
          return;
        }

        const finish = () => {
          for (const key of keys) leaving.current.delete(key);
          commit();
        };
        void Promise.all(running).then(finish, finish);
      },
    }),
    [],
  );
}
